import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLElement[]} tracks
 * @property {HTMLButtonElement} btnPrev
 * @property {HTMLButtonElement} btnNext
 *
 * @extends {Component<Refs>}
 */
class CustomFeaturedTabs extends Component {
  /** @type {number} */
  #activeTab = 0;

  /** @type {Map<number, number>} tab index → current scroll index */
  #offsets = new Map();

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {number} */
  #touchStartX = 0;
  /** @type {number} */
  #touchStartY = 0;
  /** @type {number} */
  #touchStartOffset = 0;
  /** @type {boolean | null} */
  #touchIsHorizontal = null;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
  }

  #setup() {
    for (const tab of this.querySelectorAll('.ft-section__tab')) {
      tab.addEventListener('click', () => {
        const index = parseInt(/** @type {HTMLElement} */ (tab).dataset.tab ?? '0', 10);
        this.#switchTab(index);
      });
    }

    // Enable progressive image fade-in
    this.setAttribute('data-img-fade', '');

    // Wire images in the initial active panel
    const firstPanel = this.querySelector('.ft-section__panel');
    if (firstPanel) this.#wireImages(firstPanel);

    this.#resizeObserver = new ResizeObserver(() => this.#recalc());
    this.#resizeObserver.observe(this);
    this.#recalc();

    const viewport = this.querySelector('.ft-section__viewport');
    if (viewport) {
      viewport.addEventListener('touchstart', (e) => this.#onTouchStart(e), { passive: true });
      viewport.addEventListener('touchmove', (e) => this.#onTouchMove(e), { passive: false });
      viewport.addEventListener('touchend', (e) => this.#onTouchEnd(e), { passive: true });
    }
  }

  /**
   * Add img-loaded class to images in a panel as they load.
   * @param {Element} panel
   */
  #wireImages(panel) {
    for (const img of panel.querySelectorAll('.ae-card__img')) {
      const image = /** @type {HTMLImageElement} */ (img);
      if (image.complete) {
        image.classList.add('img-loaded');
      } else {
        image.addEventListener('load', () => image.classList.add('img-loaded'), { once: true });
      }
    }
  }

  /** @param {number} index */
  #switchTab(index) {
    if (index === this.#activeTab) return;

    const tabs = [...this.querySelectorAll('.ft-section__tab')];
    const panels = [...this.querySelectorAll('.ft-section__panel')];

    tabs[this.#activeTab]?.classList.remove('ft-section__tab--active');
    tabs[this.#activeTab]?.setAttribute('aria-selected', 'false');

    // Hide outgoing — remove revealed so items reset for next time
    const outgoing = panels[this.#activeTab];
    if (outgoing) {
      delete outgoing.dataset.revealed;
      outgoing.hidden = true;
    }

    this.#activeTab = index;

    tabs[index]?.classList.add('ft-section__tab--active');
    tabs[index]?.setAttribute('aria-selected', 'true');

    // Show incoming, force reflow so items start from opacity:0 / translateY,
    // then set data-revealed to trigger the stagger transition
    const incoming = panels[index];
    if (incoming) {
      incoming.hidden = false;
      incoming.offsetHeight; // force reflow
      incoming.dataset.revealed = '';
      this.#wireImages(incoming);
    }

    this.#recalc();
  }

  prev() {
    this.#go(this.#activeTab, (this.#offsets.get(this.#activeTab) ?? 0) - 1);
  }

  next() {
    this.#go(this.#activeTab, (this.#offsets.get(this.#activeTab) ?? 0) + 1);
  }

  /** @param {number} tabIndex @param {number} index */
  #go(tabIndex, index) {
    const track = this.refs.tracks?.[tabIndex];
    if (!track) return;

    const items = [...track.querySelectorAll('.ft-section__item')];
    if (!items.length) return;

    const cols = this.#getColumns();
    const max = Math.max(0, items.length - cols);
    const clamped = Math.min(Math.max(0, index), max);

    this.#offsets.set(tabIndex, clamped);

    const itemWidth = items[0].offsetWidth;
    const gap = parseFloat(getComputedStyle(track).gap) || 16;
    const offset = -clamped * (itemWidth + gap);

    track.style.setProperty('--ft-offset', `${offset}px`);

    if (this.refs.btnPrev) this.refs.btnPrev.disabled = clamped <= 0;
    if (this.refs.btnNext) this.refs.btnNext.disabled = clamped >= max;
  }

  /** @param {TouchEvent} e */
  #onTouchStart(e) {
    this.#touchStartX = e.touches[0].clientX;
    this.#touchStartY = e.touches[0].clientY;
    this.#touchIsHorizontal = null;

    const track = this.refs.tracks?.[this.#activeTab];
    if (!track) return;

    this.#touchStartOffset = parseFloat(track.style.getPropertyValue('--ft-offset')) || 0;
    track.style.transition = 'none';
  }

  /** @param {TouchEvent} e */
  #onTouchMove(e) {
    const track = this.refs.tracks?.[this.#activeTab];
    if (!track) return;

    const deltaX = e.touches[0].clientX - this.#touchStartX;
    const deltaY = e.touches[0].clientY - this.#touchStartY;

    if (this.#touchIsHorizontal === null) {
      this.#touchIsHorizontal = Math.abs(deltaX) > Math.abs(deltaY);
    }

    if (!this.#touchIsHorizontal) {
      track.style.transition = '';
      return;
    }

    e.preventDefault();
    track.style.setProperty('--ft-offset', `${this.#touchStartOffset + deltaX}px`);
  }

  /** @param {TouchEvent} e */
  #onTouchEnd(e) {
    const track = this.refs.tracks?.[this.#activeTab];
    if (!track) return;

    track.style.transition = '';

    if (!this.#touchIsHorizontal) return;

    const deltaX = e.changedTouches[0].clientX - this.#touchStartX;
    const threshold = 50;
    const current = this.#offsets.get(this.#activeTab) ?? 0;

    if (deltaX < -threshold) {
      this.#go(this.#activeTab, current + 1);
    } else if (deltaX > threshold) {
      this.#go(this.#activeTab, current - 1);
    } else {
      this.#go(this.#activeTab, current);
    }

    this.#touchIsHorizontal = null;
  }

  #getColumns() {
    return Math.round(parseFloat(getComputedStyle(this).getPropertyValue('--ft-active-columns')) || 4);
  }

  #recalc() {
    const track = this.refs.tracks?.[this.#activeTab];
    if (!track) return;

    const viewport = track.closest('.ft-section__viewport');
    if (!viewport) return;

    const cols = parseFloat(getComputedStyle(this).getPropertyValue('--ft-active-columns')) || 4;
    const trackStyle = getComputedStyle(track);
    const gap = parseFloat(trackStyle.gap) || 16;
    const trackPadding = parseFloat(trackStyle.paddingLeft) + parseFloat(trackStyle.paddingRight);
    const viewportWidth = /** @type {HTMLElement} */ (viewport).offsetWidth - trackPadding;
    const itemWidth = (viewportWidth - gap * (Math.ceil(cols) - 1)) / cols;

    this.style.setProperty('--ft-item-width', `${itemWidth}px`);

    const current = this.#offsets.get(this.#activeTab) ?? 0;
    this.#go(this.#activeTab, current);
  }
}

if (!customElements.get('custom-featured-tabs')) {
  customElements.define('custom-featured-tabs', CustomFeaturedTabs);
}
